/**
 * Single network call for weekly working hours.
 * Endpoint: GET `ats_interviews/interviewers_schedule`.
 *
 * Returns a map from `userId → WorkingHoursInterval[]`.
 * Does **not** touch React / React Query — safe to reuse from tests,
 * workers, or plain node scripts.
 */
import moment from 'moment-timezone';
import { api } from '@/api/request';
import { normalizeTimeZoneForAtsCalendarApi } from '@/api/calendarEvents';
import type {
  InterviewerScheduleByUserId,
  InterviewerScheduleRow,
} from './types';

export interface FetchInterviewerScheduleArgs {
  /** Any ISO timestamp inside the target week. Normalised to the Monday start in `timezone`. */
  weekStartIso: string;
  timezone: string;
  interviewerIds: number[];
}

export async function fetchInterviewerSchedule(
  args: FetchInterviewerScheduleArgs
): Promise<InterviewerScheduleByUserId> {
  if (args.interviewerIds.length === 0) {
    return {};
  }

  const tz = normalizeTimeZoneForAtsCalendarApi(args.timezone);
  // Legacy ATS contract (see KInterviewScheduler useScheduleLogic.tsx):
  // the backend expects the **Sunday** 00:00 in the panel timezone, not
  // Monday/ISO week start. Using the wrong weekday returns
  // `err_invalid_week_start_date`.
  const weekStart = moment(args.weekStartIso)
    .tz(tz)
    .startOf('week')
    .format('YYYY-MM-DDTHH:mm:ssZ');

  // The axios client serialises arrays with bracket notation so
  // `interviewer_ids: [1,2]` becomes `interviewer_ids[]=1&interviewer_ids[]=2`.
  // Default unwrapData=true handles the `{ data: { interviewers: [...] } }`
  // envelope the runtime returns; when the body is flat it falls through.
  const body = await api.get<{
    interviewers?: InterviewerScheduleRow[];
  }>('ats_interviews/interviewers_schedule', {
    params: {
      week_start_date: weekStart,
      time_zone: tz,
      interviewer_ids: args.interviewerIds,
    },
  });
  const rows = body.interviewers ?? [];

  const out: InterviewerScheduleByUserId = {};
  for (const row of rows) {
    const id = row.interviewer_id;
    if (typeof id !== 'number' || !Number.isFinite(id)) {
      continue;
    }
    out[String(id)] = (row.interviewer_weekly_available_intervals ?? []).map(
      ({ start, end }) => ({ start, end })
    );
  }
  return out;
}
